import { type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '@bravemove/i18n/react';
import { Button, CurrencyDisplay, QueueCard, Sparkline } from '@bravemove/ui-web';
import { UIIcon, type UIIconName } from '@bravemove/design-tokens/icons';
import type { AnalyticsDashboard, FinanceSummary, Permission } from '@bravemove/types';
import { useAuth } from '../auth/useAuth';
import type { AdminAuditEntry } from '../auth/types';
import { PageHeader } from '../components/PageHeader';
// F27 (Wave 2E) — rolling-30d WhatsApp cost summary widget (real, perm-gated).
import { WhatsAppCostSummaryCard } from '../components/whatsapp/WhatsAppCostSummaryCard';
import { usePlatformFeatures } from '../hooks/usePlatformFeatures';
import { useDashboardData } from '../hooks/useDashboardData';
import { useDashboardQueues } from '../hooks/useDashboardQueues';

// ─── small formatters ─────────────────────────────────────────────────────────

function fmtNum(n: number | undefined, locale: string): string {
  return n === undefined ? '—' : new Intl.NumberFormat(locale).format(n);
}

function fmtCompact(n: number | undefined, locale: string): string {
  return n === undefined
    ? '—'
    : new Intl.NumberFormat(locale, { notation: 'compact', maximumFractionDigits: 1 }).format(n);
}

function fmtTime(iso: string, locale: string): string {
  try {
    return new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit' }).format(
      new Date(iso),
    );
  } catch {
    return '';
  }
}

function fmtDateTime(iso: string, locale: string): string {
  try {
    return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(
      new Date(iso),
    );
  } catch {
    return iso;
  }
}

function analyticsKpi(a: AnalyticsDashboard | null, key: string) {
  return a?.kpis.find((k) => k.key === key);
}

function financeKpi(f: FinanceSummary | null, key: string) {
  return f?.kpis.find((k) => k.key === key);
}

// ─── component ────────────────────────────────────────────────────────────────

interface ModuleLine {
  readonly label: string;
  readonly value: ReactNode;
}

interface ModuleSpec {
  readonly id: string;
  readonly title: string;
  readonly icon: UIIconName;
  readonly eyebrow: string;
  readonly headline: ReactNode;
  readonly caption: string;
  readonly route: string;
  readonly sparkline: readonly number[] | undefined;
  readonly lines: readonly ModuleLine[];
  readonly accentVar: string;
}

export function DashboardHome() {
  const { t, locale } = useI18n();
  const { whatsapp: whatsappEnabled } = usePlatformFeatures();
  const navigate = useNavigate();
  const { adminUser } = useAuth();

  const permissions: readonly Permission[] = adminUser?.permissions ?? [];
  const data = useDashboardData(permissions);
  const queues = useDashboardQueues(permissions);

  const canVenues = permissions.includes('venues:read');
  const canUsers = permissions.includes('users:read');
  const canAnalytics = permissions.includes('analytics:read');
  const canFinance = permissions.includes('financial:read');
  const canAudit = permissions.includes('audit:read');

  if (!adminUser) return null;

  const greeting = adminUser.displayName
    ? t('admin:dashboard.greetingNamed', { name: adminUser.displayName })
    : t('admin:dashboard.greeting');

  const bookings = analyticsKpi(data.analytics, 'bookings');
  const activeUsersKpi = analyticsKpi(data.analytics, 'active_users');
  const netRevenue = financeKpi(data.finance, 'net_revenue');
  const grossRevenue = financeKpi(data.finance, 'gross_revenue');

  const money = (minor: number): ReactNode => (
    <CurrencyDisplay amount={minor} currency="USD" locale={locale} />
  );

  // ─── Module cards (real headline + sub-metrics + trend where available) ─────
  const lbl = {
    total: t('admin:dashboard.label.total', { defaultValue: 'Total' }),
    flagged: t('admin:dashboard.label.flagged', { defaultValue: 'Flagged' }),
    gross: t('admin:dashboard.label.gross', { defaultValue: 'Gross · 30d' }),
    bookings: t('admin:dashboard.label.bookings', { defaultValue: 'Bookings · 30d' }),
    signups: t('admin:dashboard.label.signups', { defaultValue: 'Signups · 30d' }),
  };

  const signups30d = data.analytics
    ? data.analytics.signupsByDay.reduce((s, d) => s + d.count, 0)
    : undefined;

  const modules: ModuleSpec[] = [];
  if (canVenues) {
    modules.push({
      id: 'venues',
      title: t('admin:nav.venues'),
      icon: 'pin',
      eyebrow: t('admin:dashboard.module.venuesEyebrow', { defaultValue: 'Catalogue' }),
      headline: fmtNum(data.venues?.active, locale),
      caption: t('admin:dashboard.module.venuesCaption', { defaultValue: 'Active venues' }),
      route: '/venues',
      sparkline: undefined,
      lines: [
        { label: lbl.total, value: fmtNum(data.venues?.total, locale) },
        { label: lbl.flagged, value: fmtNum(data.venues?.flagged, locale) },
      ],
      accentVar: '--color-secondary',
    });
  }
  if (canUsers) {
    modules.push({
      id: 'users',
      title: t('admin:nav.users'),
      icon: 'team',
      eyebrow: t('admin:dashboard.module.usersEyebrow', { defaultValue: 'Directory' }),
      headline: fmtCompact(data.users?.total, locale),
      caption: t('admin:dashboard.module.usersCaption', { defaultValue: 'Active accounts' }),
      route: '/users',
      sparkline: data.analytics ? data.analytics.signupsByDay.map((d) => d.count) : undefined,
      lines: [{ label: lbl.signups, value: fmtNum(signups30d, locale) }],
      accentVar: '--color-info',
    });
  }
  if (canFinance) {
    modules.push({
      id: 'finance',
      title: t('admin:nav.finance'),
      icon: 'wallet',
      eyebrow: t('admin:dashboard.module.financeEyebrow', { defaultValue: 'Revenue' }),
      headline: netRevenue ? money(netRevenue.amountMinor) : '—',
      caption: t('admin:dashboard.module.financeCaption', { defaultValue: 'Net · last 30 days' }),
      route: '/finance',
      sparkline: data.finance ? data.finance.trend.map((d) => d.netMinor) : undefined,
      lines: grossRevenue ? [{ label: lbl.gross, value: money(grossRevenue.amountMinor) }] : [],
      accentVar: '--color-success',
    });
  }
  if (canAnalytics) {
    modules.push({
      id: 'analytics',
      title: t('admin:nav.analytics'),
      icon: 'cup',
      eyebrow: t('admin:dashboard.module.analyticsEyebrow', { defaultValue: 'Engagement' }),
      headline: fmtCompact(activeUsersKpi?.value, locale),
      caption: t('admin:dashboard.module.analyticsCaption', {
        defaultValue: 'Active users · last 30 days',
      }),
      route: '/analytics',
      sparkline: data.analytics ? data.analytics.activeUsersTrend.map((d) => d.dau) : undefined,
      lines: [{ label: lbl.bookings, value: fmtNum(bookings?.value, locale) }],
      accentVar: '--color-tertiary',
    });
  }

  // ─── "Needs your eyes" — real pending-queue counts (no ICU interpolation) ──
  const pendingWord = t('admin:dashboard.queuePending', { defaultValue: 'pending' });
  const eyesRows = queues.slice(0, 6).map((q) => ({
    id: q.route,
    label: t(q.labelKey),
    meta: `${q.count} ${pendingWord}`,
    accent: q.count >= 10 ? ('urgent' as const) : ('normal' as const),
  }));

  const lastUpdated = data.analytics?.lastDailyRefreshedAt ?? data.finance?.lastRefreshedAt ?? null;

  return (
    <div className="space-y-6">
      <PageHeader title={greeting} subtitle={t('admin:dashboard.subtitle')} />

      {/* Module summary cards — the primary at-a-glance row (headline + real
          sub-metrics + trend), each a clickable entry point into its module. */}
      {modules.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-start">
          {modules.map((m) => (
            <ModuleCard key={m.id} spec={m} onOpen={() => navigate(m.route)} />
          ))}
        </div>
      ) : null}

      {/* Activity log + "Needs your eyes" panel */}
      <div className="grid grid-cols-1 lg:grid-cols-[1.5fr_1fr] gap-4 items-start">
        <div className="rounded-2xl bg-surface border border-hairline p-5">
          <div className="flex justify-between items-baseline mb-3">
            <div className="text-[10px] font-mono font-bold uppercase tracking-wider text-ink-3">
              {t('admin:dashboard.recentActivity')}
            </div>
            {canAudit ? (
              <Button variant="link" size="sm" onClick={() => navigate('/audit-log')}>
                {t('admin:dashboard.fullLog')}
              </Button>
            ) : null}
          </div>
          {data.activity.length === 0 ? (
            <p className="text-[13px] text-ink-3 py-8 text-center">
              {data.loading
                ? t('admin:dashboard.activityLoading', { defaultValue: 'Loading…' })
                : t('admin:dashboard.activityEmpty', { defaultValue: 'No recent admin activity.' })}
            </p>
          ) : (
            <ul className="flex flex-col divide-y divide-hairline">
              {data.activity.map((r: AdminAuditEntry) => (
                <li key={r.id} className="flex items-center gap-3 py-2.5">
                  <span className="w-12 text-[10px] font-mono uppercase tracking-wider text-ink-3 shrink-0">
                    {fmtTime(r.createdAt, locale)}
                  </span>
                  <div className="flex-1 min-w-0 text-[13px] text-ink-2 truncate">
                    <strong className="text-ink">{r.actorEmail}</strong>{' '}
                    <span className="font-mono text-[12px] text-accent">{r.action}</span>
                    {r.targetType ? (
                      <span className="text-ink-3">
                        {' '}
                        · {r.targetType}
                        {r.targetId ? `:${r.targetId.slice(0, 8)}` : ''}
                      </span>
                    ) : null}
                  </div>
                  <span
                    className={`shrink-0 rounded font-mono text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 border ${
                      r.status === 'success'
                        ? 'bg-success/15 text-success border-success/30'
                        : 'bg-danger/15 text-danger border-danger/30'
                    }`}
                  >
                    {t(`admin:audit.status.${r.status}`, { defaultValue: r.status })}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {eyesRows.length > 0 ? (
          <QueueCard
            eyebrow={t('admin:dashboard.queueEyebrow', { defaultValue: 'Queue for you' })}
            title={t('admin:dashboard.needsYourEyes')}
            rows={eyesRows}
            onRowClick={(id) => navigate(id)}
          />
        ) : (
          <div className="rounded-2xl bg-surface border border-hairline p-6 flex flex-col items-center justify-center gap-2 text-center">
            <div className="text-[14px] font-bold text-ink">
              {t('admin:dashboard.allClearTitle', { defaultValue: 'All clear' })}
            </div>
            <p className="text-[12px] text-ink-2 max-w-[240px]">
              {data.loading
                ? t('admin:dashboard.allClearLoading', { defaultValue: 'Checking your queues…' })
                : t('admin:dashboard.allClearBody', {
                    defaultValue: 'Nothing in your queues needs attention.',
                  })}
            </p>
          </div>
        )}
      </div>

      {/* F27 — WhatsApp cost summary card. Renders nothing without whatsapp:read.
          FIX 5.27 — hidden while WhatsApp delivery is deferred (features.whatsapp). */}
      {whatsappEnabled ? <WhatsAppCostSummaryCard /> : null}

      {lastUpdated ? (
        <div className="flex justify-end text-[10px] font-mono uppercase tracking-wider text-ink-3 pb-3">
          <span>
            {t('admin:dashboard.updated', { defaultValue: 'Updated' })}{' '}
            {fmtDateTime(lastUpdated, locale)}
          </span>
        </div>
      ) : null}
    </div>
  );
}

interface ModuleCardProps {
  readonly spec: ModuleSpec;
  readonly onOpen: () => void;
}

function ModuleCard({ spec, onOpen }: ModuleCardProps) {
  const showSparkline = spec.sparkline !== undefined && spec.sparkline.length > 1;
  return (
    <button
      type="button"
      onClick={onOpen}
      className="text-start rounded-2xl bg-surface border border-hairline p-5 flex flex-col gap-3 transition-shadow duration-[120ms] hover:shadow-[0_8px_24px_-12px_rgba(0,0,0,0.18)] focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-accent/30"
    >
      <div className="flex items-center gap-2.5">
        <div
          className="size-9 rounded-xl inline-flex items-center justify-center"
          style={{
            background: `color-mix(in srgb, var(${spec.accentVar}) 18%, transparent)`,
            color: `var(${spec.accentVar})`,
          }}
        >
          <UIIcon name={spec.icon} size={18} color="currentColor" />
        </div>
        <div>
          <div className="text-[15px] font-bold text-ink tracking-tight leading-tight">
            {spec.title}
          </div>
          <div className="text-[9.5px] font-mono uppercase tracking-wider text-ink-3 mt-0.5">
            {spec.eyebrow}
          </div>
        </div>
      </div>
      <div>
        <div className="text-[32px] font-extrabold leading-none tracking-tight text-ink tabular-nums">
          {spec.headline}
        </div>
        <div className="text-[10px] font-mono uppercase tracking-wider text-ink-3 mt-1.5">
          {spec.caption}
        </div>
      </div>
      {showSparkline ? (
        <Sparkline
          data={spec.sparkline as readonly number[]}
          width={220}
          height={36}
          variant="area"
          colorVar={spec.accentVar}
          className="w-full"
        />
      ) : spec.lines.length > 0 ? (
        <div className="border-t border-hairline pt-2.5 flex flex-col gap-1.5">
          {spec.lines.map((l) => (
            <div key={l.label} className="flex justify-between text-[12px]">
              <span className="text-ink-3">{l.label}</span>
              <span className="text-ink font-semibold tabular-nums">{l.value}</span>
            </div>
          ))}
        </div>
      ) : null}
    </button>
  );
}
